import { SystemError } from '../../errors';
import type { ElemPredicate, Predicate, Scalar } from './predicate';

type EncodedScalar = readonly ['string', string] | readonly ['number', string] | readonly ['boolean', boolean];

const encodeScalar = (value: Scalar): EncodedScalar => {
  if (typeof value === 'string') return ['string', value];
  if (typeof value === 'boolean') return ['boolean', value];
  if (Number.isNaN(value)) return ['number', 'NaN'];
  if (value === Number.POSITIVE_INFINITY) return ['number', 'Infinity'];
  if (value === Number.NEGATIVE_INFINITY) return ['number', '-Infinity'];
  if (Object.is(value, -0)) return ['number', '-0'];
  return ['number', String(value)];
};

const encodeField = (field: string): readonly ['field', string] => ['field', field];
const encodePath = (path: readonly string[]): readonly (readonly ['segment', string])[] =>
  path.map((segment) => ['segment', segment]);

const serialize = (value: readonly unknown[]): string => {
  const serialized = JSON.stringify(value);
  if (serialized === undefined) {
    throw new SystemError({
      code: 'SEARCH_PREDICATE_KEY_UNSERIALIZABLE',
      userMessage: 'Predicate key could not be serialized',
      retryable: false,
    });
  }
  return serialized;
};

const sortCanonicalValues = (values: readonly Scalar[]): readonly EncodedScalar[] =>
  values.map(encodeScalar).sort((left, right) => {
    const leftKey = serialize(left);
    const rightKey = serialize(right);
    return leftKey < rightKey ? -1 : leftKey > rightKey ? 1 : 0;
  });

function encodeElemPredicate(p: ElemPredicate): readonly unknown[] {
  switch (p.kind) {
    case 'eq':
      return [p.kind, encodeField(p.field), encodeScalar(p.value)];
    case 'cmp':
      return [p.kind, encodeField(p.field), p.op, encodeScalar(p.value)];
    case 'and':
      return [p.kind, p.operands.map((operand) => serialize(encodeElemPredicate(operand))).sort()];
  }
}

function encodePredicate<T>(p: Predicate<T>): readonly unknown[] {
  switch (p.kind) {
    case 'always':
    case 'never':
      return [p.kind];
    case 'eq':
    case 'eqStringLiteral':
    case 'neq':
      return [p.kind, encodeField(p.field), encodeScalar(p.value)];
    case 'in':
      return [p.kind, encodeField(p.field), sortCanonicalValues(p.values)];
    case 'cmp':
      return [p.kind, encodeField(p.field), p.op, encodeScalar(p.value)];
    case 'pathCmp':
      return [p.kind, encodePath(p.path), p.op, encodeScalar(p.value)];
    case 'elemMatch':
      return [p.kind, encodeField(p.field), encodeElemPredicate(p.where)];
    case 'not':
      return [p.kind, filterKey(p.operand)];
    case 'and':
    case 'or':
      return [p.kind, p.operands.map(filterKey).sort()];
  }
}

/** Stable, order-insensitive serialisation. Equal predicates ⇒ equal keys. */
export function filterKey<T>(p: Predicate<T>): string {
  return serialize(encodePredicate(p));
}
